/**
 * LabelPrintModal - Reusable label printing modal for bulk printing
 * from any data table (locations, items, etc.)
 *
 * Features:
 * - 4-level hierarchical configuration via printingService
 * - PrinterSelect component for printer selection
 * - Display options (header, barcode, description, zone)
 * - PDF preview via /report/generate endpoint
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Printer, Tag, MapPin, Loader2, Check,
  AlertCircle, Minus, Plus, ChevronDown, Eye, ChevronLeft, ChevronRight
} from 'lucide-react';
import { PrinterSelect } from './PrinterSelect';
import { printingService } from '../../services/printingService';
import { AuthService } from '../../services/authService';
import type { Report } from '../../types/printing';
import { useAuth } from '../../contexts/AuthContext';
import { useWarehouse } from '../../contexts/WarehouseContext';
import { ClampedIntegerInput } from './inputs';

// ==================== TYPES ====================

export interface LabelPrintItem {
  id: string;
  displayName: string;
  // Item-specific fields
  itemNo?: string;
  itemDescription?: string;
  // Location-specific fields
  binCode?: string;
  locationName?: string;
  warehouseId?: string;
  zoneName?: string;
  description?: string;
}

interface LabelPrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  printType: 'item' | 'location';
  items: LabelPrintItem[];
  defaultPrinterId?: string | null;
}

interface PrintProgress {
  current: number;
  total: number;
  successes: number;
  errors: { displayName: string; message: string }[];
}

// ==================== COMPONENT ====================

export const LabelPrintModal: React.FC<LabelPrintModalProps> = ({
  isOpen,
  onClose,
  printType,
  items,
  defaultPrinterId = null,
}) => {
  const { user } = useAuth();
  // Vareetiketter: lageret operatøren står på gir varens lokasjon der (null = «Alle lager»)
  const { currentWarehouse } = useWarehouse();

  // Form state
  const [selectedPrinterId, setSelectedPrinterId] = useState<string | null>(defaultPrinterId);
  const [copies, setCopies] = useState(1);
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [includeBarcode, setIncludeBarcode] = useState(true);

  // Display options
  const [showHeader, setShowHeader] = useState(false);
  const [showDescription, setShowDescription] = useState(true);
  const [showZone, setShowZone] = useState(false);

  // Reports state
  const [reports, setReports] = useState<Report[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);

  // Printing state
  const [isPrinting, setIsPrinting] = useState(false);
  const [progress, setProgress] = useState<PrintProgress | null>(null);
  const [completed, setCompleted] = useState(false);

  // Preview state
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [showPreview, setShowPreview] = useState(false);

  // Load available report templates
  useEffect(() => {
    if (!isOpen || !user?.companyNo) return;

    const loadReports = async () => {
      setLoadingReports(true);
      try {
        // ReportType: 1 = item labels, 4 = location labels
        const reportType = printType === 'item' ? 1 : 4;
        const result = await printingService.getReports(user.companyNo!, reportType, true);
        setReports(result);
        if (result.length > 0 && !selectedReportId) {
          setSelectedReportId(result[0].reportID);
        }
      } catch {
        // Non-critical - user can still print without report selection
        setReports([]);
      } finally {
        setLoadingReports(false);
      }
    };

    loadReports();
  }, [isOpen, user?.companyNo, printType]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCopies(1);
      setIsPrinting(false);
      setProgress(null);
      setCompleted(false);
      setSelectedReportId(null);
      setShowPreview(false);
      setPreviewUrl('');
      setPreviewError(null);
      setPreviewIndex(0);
      setShowHeader(false);
      setShowDescription(true);
      setShowZone(false);
      if (defaultPrinterId) {
        setSelectedPrinterId(defaultPrinterId);
      }
    }
  }, [isOpen, defaultPrinterId]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Build display options for requests
  const getDisplayOptions = useCallback(() => {
    if (printType === 'item') {
      return { showHeader, showBarcode: includeBarcode, showDescription };
    }
    return { showHeader, showDescription, showZone, includeBarcode };
  }, [printType, showHeader, includeBarcode, showDescription, showZone]);

  // Generate preview for a single item
  const generatePreview = useCallback(async (index: number) => {
    if (!user?.companyNo || items.length === 0) return;

    const item = items[index];
    setPreviewLoading(true);
    setPreviewError(null);

    // Clean up old URL
    if (previewUrl) URL.revokeObjectURL(previewUrl);

    try {
      const apiBase = process.env.REACT_APP_API_URL || '/api/v1';
      let body: any;

      if (printType === 'item') {
        body = {
          reportType: 2, // ItemLabel
          printerId: 0,
          // Hvis valgt rapport har LayoutJson, brukes designer-mal i stedet for itemLabel-feltene under
          reportId: selectedReportId ?? undefined,
          context: {
            itemNo: item.itemNo || item.id,
            warehouseID: currentWarehouse || undefined
          },
          itemLabel: {
            itemNo: item.itemNo || item.id,
            itemDescription: item.itemDescription || '',
            pageWidthMm: 100,
            pageHeightMm: 50,
            showHeader,
            showBarcode: includeBarcode,
            showDescription,
          }
        };
      } else {
        body = {
          reportType: 1, // ShelfLabel
          printerId: 0,
          // Hvis valgt rapport har LayoutJson, brukes designer-mal i stedet for shelfLabel-feltene under
          reportId: selectedReportId ?? undefined,
          context: {
            binCode: item.binCode || item.id,
            warehouseID: item.warehouseId || undefined
          },
          shelfLabel: {
            contentType: includeBarcode ? 4 : 3, // LocationNameWithBarcode or LocationNameOnly
            binCode: item.binCode || item.id,
            locationName: item.locationName || item.binCode || item.id,
            itemDescription: item.description || '',
            zoneName: item.zoneName || '',
            pageWidthMm: 100,
            pageHeightMm: 50,
            showHeader,
            showDescription,
            showZone,
          }
        };
      }

      const response = await AuthService.authenticatedPost(
        `${apiBase}/${user.companyNo}/report/generate`,
        body
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(err.message || `Feil ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.pdfBase64) {
        const byteCharacters = atob(data.pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      } else {
        throw new Error(data.message || 'Kunne ikke generere forhåndsvisning');
      }
    } catch (err: any) {
      setPreviewError(err.message || 'Ukjent feil ved forhåndsvisning');
    } finally {
      setPreviewLoading(false);
    }
  }, [user?.companyNo, items, printType, showHeader, includeBarcode, showDescription, showZone, selectedReportId, previewUrl, currentWarehouse]);

  // Handle preview button click
  const handlePreview = useCallback(() => {
    setShowPreview(true);
    setPreviewIndex(0);
    generatePreview(0);
  }, [generatePreview]);

  // Re-generer preview automatisk når brukeren bytter mal i dropdown (mens preview er åpent)
  useEffect(() => {
    if (!showPreview) return;
    generatePreview(previewIndex);
    // generatePreview/previewIndex utelatt for å unngå dobbel-trigger ved navigasjon
    // (handlePreviewNav kaller den selv)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReportId]);

  // Navigate preview
  const handlePreviewNav = useCallback((direction: 'prev' | 'next') => {
    const newIndex = direction === 'prev'
      ? Math.max(0, previewIndex - 1)
      : Math.min(items.length - 1, previewIndex + 1);
    setPreviewIndex(newIndex);
    generatePreview(newIndex);
  }, [previewIndex, items.length, generatePreview]);

  // Print all items sequentially
  const handlePrint = useCallback(async () => {
    if (!user?.companyNo || !selectedPrinterId || items.length === 0) return;

    setIsPrinting(true);
    setCompleted(false);
    setShowPreview(false);

    const progressState: PrintProgress = {
      current: 0,
      total: items.length,
      successes: 0,
      errors: [],
    };
    setProgress({ ...progressState });

    const displayOpts = getDisplayOptions();

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      progressState.current = i + 1;

      try {
        if (printType === 'location') {
          await printingService.printLocationLabel(user.companyNo!, {
            locationId: item.binCode || item.id,
            locationName: item.locationName || item.binCode || item.id,
            binCode: item.binCode || item.id,
            warehouseId: item.warehouseId,
            zoneName: item.zoneName,
            includeBarcode,
            numOfCopies: copies,
            reportId: selectedReportId || undefined,
            printerIdOverride: parseInt(selectedPrinterId),
            showHeader: displayOpts.showHeader,
            showDescription: displayOpts.showDescription,
            showZone: 'showZone' in displayOpts ? displayOpts.showZone : undefined,
            description: item.description,
          });
        } else {
          await printingService.printItemLabel(user.companyNo!, {
            itemNo: item.itemNo || item.id,
            itemDescription: item.itemDescription,
            warehouseId: currentWarehouse,
            numOfCopies: copies,
            reportId: selectedReportId || undefined,
            printerIdOverride: parseInt(selectedPrinterId),
            showHeader: displayOpts.showHeader,
            showBarcode: 'showBarcode' in displayOpts ? displayOpts.showBarcode : undefined,
            showDescription: displayOpts.showDescription,
          });
        }
        progressState.successes++;
      } catch (err: any) {
        progressState.errors.push({
          displayName: item.displayName,
          message: err.message || 'Ukjent feil',
        });
      }

      setProgress({ ...progressState });
    }

    setIsPrinting(false);
    setCompleted(true);
  }, [user?.companyNo, selectedPrinterId, items, printType, copies, selectedReportId, includeBarcode, getDisplayOptions, currentWarehouse]);

  if (!isOpen) return null;

  const typeLabel = printType === 'item' ? 'vare' : 'lokasjons';
  const TypeIcon = printType === 'item' ? Tag : MapPin;
  const iconColor = printType === 'item' ? 'text-blue-600' : 'text-teal-600';
  const bgColor = printType === 'item' ? 'bg-blue-50' : 'bg-teal-50';
  const accentColor = printType === 'item' ? 'text-blue-600' : 'text-teal-600';
  const checkboxColor = printType === 'item' ? 'text-blue-600' : 'text-teal-600';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className={`bg-white rounded-xl shadow-2xl w-full mx-4 ${showPreview ? 'max-w-4xl' : 'max-w-lg'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 ${bgColor} rounded-t-xl border-b`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 bg-white rounded-lg shadow-sm ${iconColor}`}>
              <TypeIcon size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                Skriv ut {typeLabel}etiketter
              </h3>
              <p className="text-sm text-gray-600">
                {items.length} {items.length === 1 ? 'etikett' : 'etiketter'} valgt
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-white/60 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className={`${showPreview ? 'flex' : ''}`}>
          {/* Settings panel */}
          <div className={`px-6 py-5 space-y-5 ${showPreview ? 'w-1/2 border-r border-gray-200 overflow-y-auto max-h-[70vh]' : ''}`}>
            {!completed ? (
              <>
                {/* Printer Selection */}
                <PrinterSelect
                  value={selectedPrinterId}
                  onChange={setSelectedPrinterId}
                  label="Velg skriver"
                  showTestButton={!isPrinting}
                  disabled={isPrinting}
                  size="sm"
                />

                {/* Report Type Selection */}
                {reports.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Etikettmal
                    </label>
                    <div className="relative">
                      <select
                        value={selectedReportId ?? ''}
                        onChange={(e) => setSelectedReportId(e.target.value ? parseInt(e.target.value) : null)}
                        disabled={isPrinting}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white appearance-none
                          focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-50"
                      >
                        {reports.map((r) => (
                          <option key={r.reportID} value={r.reportID}>
                            {r.description || r.reportName || `Rapport ${r.reportID}`}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                )}

                {/* Copies */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Antall kopier per etikett
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCopies(Math.max(1, copies - 1))}
                      disabled={isPrinting || copies <= 1}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50
                        disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Minus size={16} />
                    </button>
                    <ClampedIntegerInput
                      value={copies}
                      onValueChange={setCopies}
                      min={1}
                      max={100}
                      disabled={isPrinting}
                      className="w-20 min-h-[44px] px-3 text-base text-center border border-gray-300 rounded-lg
                        focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                    />
                    <button
                      onClick={() => setCopies(Math.min(100, copies + 1))}
                      disabled={isPrinting || copies >= 100}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50
                        disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                {/* Etikettinnhold-valgene er fjernet — innholdet styres nå av valgt Etikettmal
                    (Reports.LayoutJson). Fallback-verdier (includeBarcode=true, showDescription=true,
                    showHeader=false, showZone=false) brukes hvis malen ikke har LayoutJson. */}

                {/* Progress bar during printing */}
                {isPrinting && progress && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 flex items-center gap-2">
                        <Loader2 size={14} className="animate-spin" />
                        Skriver ut {progress.current} av {progress.total}...
                      </span>
                      <span className="text-gray-500 font-medium">
                        {Math.round((progress.current / progress.total) * 100)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(progress.current / progress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Completion summary */
              <div className="space-y-4">
                {progress && progress.errors.length === 0 ? (
                  <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <Check size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-green-800">
                        Alle etiketter skrevet ut
                      </p>
                      <p className="text-sm text-green-600 mt-0.5">
                        {progress.successes} {printType === 'item' ? 'vare' : 'lokasjons'}etikett{progress.successes !== 1 ? 'er' : ''} sendt til skriver
                        {copies > 1 && ` (${copies} kopier hver)`}
                      </p>
                    </div>
                  </div>
                ) : progress && (
                  <div className="space-y-3">
                    {progress.successes > 0 && (
                      <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <Check size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-green-700">
                          {progress.successes} av {progress.total} etiketter skrevet ut
                        </p>
                      </div>
                    )}
                    <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-red-800">
                          {progress.errors.length} etikett{progress.errors.length !== 1 ? 'er' : ''} feilet
                        </p>
                        <ul className="mt-1 space-y-0.5">
                          {progress.errors.slice(0, 5).map((err, i) => (
                            <li key={i} className="text-xs text-red-600">
                              {err.displayName}: {err.message}
                            </li>
                          ))}
                          {progress.errors.length > 5 && (
                            <li className="text-xs text-red-500 italic">
                              ...og {progress.errors.length - 5} til
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Preview panel */}
          {showPreview && (
            <div className="w-1/2 flex flex-col">
              {/* Preview header with navigation */}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <span className="text-sm font-medium text-gray-700">Forhåndsvisning</span>
                {items.length > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePreviewNav('prev')}
                      disabled={previewIndex === 0 || previewLoading}
                      className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 transition-colors"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span className="text-xs text-gray-500 tabular-nums">
                      {previewIndex + 1} / {items.length}
                    </span>
                    <button
                      onClick={() => handlePreviewNav('next')}
                      disabled={previewIndex === items.length - 1 || previewLoading}
                      className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 transition-colors"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Lukk forhåndsvisning"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Preview item name */}
              <div className="px-4 py-2 bg-white border-b border-gray-100">
                <p className="text-xs text-gray-500 truncate">{items[previewIndex]?.displayName}</p>
              </div>

              {/* Preview content */}
              <div className="flex-1 bg-gray-100 relative min-h-[350px]">
                {previewLoading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <Loader2 className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Genererer...</p>
                    </div>
                  </div>
                )}

                {previewError && !previewLoading && (
                  <div className="absolute inset-0 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow p-4 max-w-xs text-center">
                      <AlertCircle className="text-red-500 mx-auto mb-2" size={24} />
                      <p className="text-sm text-gray-700 mb-3">{previewError}</p>
                      <button
                        onClick={() => generatePreview(previewIndex)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Pr&oslash;v igjen
                      </button>
                    </div>
                  </div>
                )}

                {previewUrl && !previewLoading && !previewError && (
                  <iframe
                    src={previewUrl}
                    className="w-full h-full border-0 min-h-[350px]"
                    title="Etikett forhåndsvisning"
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 rounded-b-xl border-t">
          <div>
            {!completed && !isPrinting && items.length > 0 && (
              <button
                onClick={showPreview ? () => { setShowPreview(false); setPreviewUrl(''); } : handlePreview}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors
                  ${showPreview
                    ? 'text-gray-600 bg-white border border-gray-300 hover:bg-gray-50'
                    : `${accentColor} bg-white border border-gray-300 hover:bg-gray-50`
                  }`}
              >
                <Eye size={16} />
                {showPreview ? 'Skjul forhåndsvisning' : 'Forhåndsvis'}
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {completed ? (
              <button
                onClick={onClose}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-gray-700 rounded-lg
                  hover:bg-gray-800 transition-colors"
              >
                Lukk
              </button>
            ) : (
              <>
                <button
                  onClick={onClose}
                  disabled={isPrinting}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300
                    rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  onClick={handlePrint}
                  disabled={isPrinting || !selectedPrinterId || items.length === 0}
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg
                    hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors
                    flex items-center gap-2"
                >
                  {isPrinting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Skriver ut...
                    </>
                  ) : (
                    <>
                      <Printer size={16} />
                      Skriv ut {items.length} etikett{items.length !== 1 ? 'er' : ''}
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
